/**
 * Axios client factory.
 *
 * The factory is platform-agnostic: the caller supplies a `getAuthToken`
 * function so the web client can hand over a Firebase JS SDK token and the
 * mobile client can hand over a React Native Firebase token, without this
 * package ever importing either SDK.
 */

import axios from 'axios';
import { normalizeApiError, ApiError } from './errors.js';

const DEFAULT_TIMEOUT_MS = 20000;

/**
 * Creates a configured Axios instance.
 *
 * @param {object} options
 * @param {string} options.baseURL
 * @param {() => Promise<string|null>} [options.getAuthToken] resolves a Firebase ID token
 * @param {() => Promise<string|null>} [options.refreshAuthToken] forces a token refresh
 * @param {(error: ApiError) => void} [options.onAuthError] called when the session is invalid
 * @param {(error: ApiError) => void} [options.onClientOutdated] called when the server
 *   refuses this build (426). Fires from ANY request, including ones that do
 *   not go through a query library, so an app that was running when the
 *   supported floor moved finds out at its next call rather than at its next
 *   launch. Set `skipClientOutdated` on a request to opt out.
 * @param {number} [options.timeout]
 * @param {string} [options.clientName] sent as X-Client for server-side logging
 * @param {string} [options.clientVersion]
 */
export function createApiClient({
  baseURL,
  getAuthToken,
  refreshAuthToken,
  onAuthError,
  onClientOutdated,
  timeout = DEFAULT_TIMEOUT_MS,
  clientName = 'unknown',
  clientVersion = '1.0.0',
} = {}) {
  const instance = axios.create({
    baseURL,
    timeout,
    headers: {
      'Content-Type': 'application/json',
      'X-Client': clientName,
      'X-Client-Version': clientVersion,
    },
  });

  instance.interceptors.request.use(async (config) => {
    if (config.skipAuth || typeof getAuthToken !== 'function') return config;

    try {
      const token = await getAuthToken();
      if (token) config.headers.Authorization = `Bearer ${token}`;
    } catch {
      // A missing token is not fatal here — the API will answer 401 and the
      // response interceptor handles the refresh-and-retry.
    }

    return config;
  });

  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const status = error?.response?.status;
      const original = error?.config;

      // One transparent retry after refreshing an expired ID token.
      if (status === 401 && original && !original._retried && typeof refreshAuthToken === 'function') {
        original._retried = true;
        try {
          const token = await refreshAuthToken();
          if (token) {
            original.headers = { ...original.headers, Authorization: `Bearer ${token}` };
            return await instance.request(original);
          }
        } catch {
          // Fall through to normalized error handling below.
        }
      }

      const apiError = normalizeApiError(error);
      if (apiError.isAuthError && typeof onAuthError === 'function') onAuthError(apiError);

      // Deliberately here rather than in a query library's error handler: this
      // interceptor sees every call, including the sign-in sync, the billing
      // bootstrap and the offline queue flush — and the queue flush is the most
      // likely place a client that was offline while the floor moved finds out.
      if (
        apiError.isUpdateRequired &&
        !original?.skipClientOutdated &&
        typeof onClientOutdated === 'function'
      ) {
        onClientOutdated(apiError);
      }

      return Promise.reject(apiError);
    },
  );

  return instance;
}

/**
 * Unwraps the API's `{ success, data, meta }` envelope.
 * Returns `{ data, meta }` so list callers can read pagination.
 */
export function unwrap(response) {
  const payload = response?.data;

  if (payload && typeof payload === 'object' && 'success' in payload) {
    if (payload.success) return { data: payload.data, meta: payload.meta ?? {} };

    throw new ApiError({
      code: payload.error?.code ?? 'INTERNAL_ERROR',
      message: payload.error?.message ?? 'Something went wrong.',
      details: payload.error?.details ?? {},
      status: response?.status ?? 0,
    });
  }

  return { data: payload, meta: {} };
}

/** Unwraps and returns just the `data` payload. */
export function unwrapData(response) {
  return unwrap(response).data;
}

/** Strips `undefined` values so they are not serialized as "undefined". */
export function cleanParams(params = {}) {
  const cleaned = {};
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    cleaned[key] = value;
  }
  return cleaned;
}
